import Subhead from 'components/modules/Subhead';
import Layout from 'components/template/Layout';
import { useAppContext } from 'provider/AppProvider/AppProvider';
import { IFeaturedData } from 'provider/FeaturedProvider/FeaturedProvider.interface';
import { useUserContext } from 'provider/UserProvider/UserProvider';
import { useParams } from 'react-router-dom';
import {
  useDefaultColorValue,
  useGetUserDataByUsername,
} from 'services/queries/app';
import { useGetMemberContentByType } from 'services/queries/content';
import { PageNotFoundLoggedOut } from 'pages/ActionForms/PromptMessage';
import Card from '../ContentList/Card';
import TaggedContentTypes from './TaggedContentTypes';

const MyContentType = () => {
  const { isLoading } = useGetUserDataByUsername();
  const { content_type } = useParams();
  const {
    uuid: appUuid,
    setup: {
      header: { menu },
    },
    about: { platform_version },
  } = useAppContext();
  const { loggedInUser } = useUserContext();
  const colors = useDefaultColorValue();
  const currentType = String(content_type);

  let currentTypeTitle: string;
  // Content types with renamed labels
  if (currentType === 'questions') currentTypeTitle = 'Q&A';
  switch (currentType) {
    case 'questions':
      currentTypeTitle = 'Q&A';
      break;
    default:
      currentTypeTitle = String(
        currentType.charAt(0).toUpperCase() + currentType.slice(1),
      );
  }

  const {
    data: rawContent,
    isLoading: isLoadingContents,
    status,
  } = useGetMemberContentByType(
    String(currentType),
    String(appUuid),
    String(loggedInUser?.id_token),
  );

  // console.log(rawContent);
  const availableContent = rawContent?.filter((content) => {
    const todayDate = new Date();
    // display content with blank release_date
    const releaseDate =
      content.release_date === '' ? todayDate : new Date(content.release_date);
    if (['tips', 'lessons'].includes(currentType))
      return content.active && todayDate >= releaseDate;
    return todayDate >= releaseDate;
  });

  const generateImageUrl = (item: IFeaturedData) => {
    const contentType = item.content_type ?? String(content_type);
    switch (contentType) {
      case 'products':
        return item.image_url;
      default:
        return item.image_url;
    }
  };

  let isDisabled = false;

  switch (currentType) {
    case 'action_lists':
      isDisabled =
        !menu?.enabled_modules.includes(currentType) ||
        parseFloat(platform_version) < 8.5;
      break;
    default:
      break;
  }

  const isPageNotFound =
    (status === 'success' &&
      (typeof rawContent === 'undefined' || !rawContent?.length)) ||
    isDisabled;

  if (isPageNotFound && !isLoading) {
    return <PageNotFoundLoggedOut />;
  }

  return (
    <Layout>
      <div style={{ background: colors.light }} className="h-full">
        <div className="container mx-auto px-4 py-6">
          <span className="capitalize">
            <Subhead
              backButtonHoverColor={colors.dark}
              backButtonColor={colors.medium}
              titleFontSize="2.25rem"
              title="My Content"
              activeBreadcrumbColor={colors.grey}
              breadcrumbColor={colors.dark_blue}
              breadcrumbFontSize="1rem"
              isLoading={isLoading}
            />
          </span>
          <div className="block lg:flex items-start gap-6">
            <div>
              <aside className="w-full lg:w-[432px] border border-gray-400 rounded-xl flex-col flex overflow-hidden">
                <TaggedContentTypes
                  label={currentTypeTitle}
                  path={currentType}
                  sub_topic={content_type}
                  display_content={availableContent}
                />
              </aside>
            </div>
            <section className="grow p-6 hidden lg:flex flex-col gap-8 bg-white">
              {availableContent?.length === 0 && !isLoadingContents && (
                <div className="text-gray-400">No content found.</div>
              )}
              {(availableContent ?? [])?.map((item) => (
                <Card
                  id={item.id}
                  content_type={currentType}
                  key={item.id}
                  title={item.title}
                  author={item.author}
                  description={item.description}
                  image_url={generateImageUrl(item)}
                  document_url={item.document_url}
                />
              ))}
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MyContentType;
